import { useEffect, useMemo, useState } from 'react'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import { useAuth } from '../../context/useAuth'
import { useToast } from '../../context/useToast'
import { useNls } from '../../nls/nlsProvider'
import { useFormDefinition } from '../../hooks/useFormDefinitions'
import { subscribeAssignedFormRequests } from '../../services/formRequestService'
import { registerSwitchGuard } from '../../services/workspaceSwitch/switchGuards'
import type { FormRequest, FormRequestStatus } from '../../types/forms'
import { FormRenderer } from '../Forms/FormRenderer'
import { Button, Pressable } from '../Common'
import { getHelperFormCountLabel } from '../Mobile/MobileNls'
import '../Forms/forms.css'

const statusLabel = (status: FormRequestStatus, t: ReturnType<typeof useNls>['t']) => {
  if (status === 'PENDING') return t('NLSHelperFormsStatusPending')
  if (status === 'COMPLETED') return t('NLSHelperFormsStatusCompleted')
  if (status === 'CANCELLED') return t('NLSHelperFormsStatusCancelled')
  return t('NLSHelperFormsStatusExpired')
}

export const HelperFormsPanel = ({
  workspaceId,
}: {
  workspaceId: string
  actor?: { id: string; name: string } | null
  onOpenItem?: (itemId: string) => void
}) => {
  const { t } = useNls()
  const { currentUser } = useAuth()
  const { showToast } = useToast()
  const [status, setStatus] = useState<FormRequestStatus | 'ALL'>('PENDING')
  const [requests, setRequests] = useState<FormRequest[]>([])
  const [activeRequest, setActiveRequest] = useState<FormRequest | null>(null)
  const [isFormDirty, setIsFormDirty] = useState(false)
  const activeDefinition = useFormDefinition(activeRequest?.formId ?? null)

  useEffect(() => {
    if (!activeRequest || !activeDefinition.form || !isFormDirty) return
    return registerSwitchGuard({
      id: 'helper-form',
      describe: () => (activeRequest && activeDefinition.form && isFormDirty ? t('NLSHelperFormGuardDescription') : null),
      discard: () => {
        setActiveRequest(null)
        setIsFormDirty(false)
      },
    })
  }, [activeDefinition.form, activeRequest, isFormDirty, t])

  useEffect(() => {
    if (!currentUser?.uid || !workspaceId) return
    return subscribeAssignedFormRequests(workspaceId, currentUser.uid, setRequests, (error) => showToast(error.message, 'error'))
  }, [currentUser?.uid, showToast, workspaceId])

  const visibleRequests = useMemo(
    () => requests.filter((request) => status === 'ALL' || request.status === status),
    [requests, status],
  )

  if (activeRequest && activeDefinition.form) {
    return (
      <div className="rounded-3xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-4 shadow-[var(--shadow-card)]" data-testid="helper-forms-renderer">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[var(--accent-strong)]">{t('NLSHelperFormsAssignedForm')}</p>
            <h2 className="text-lg font-black tracking-tight text-[var(--text-strong)] mt-0.5 truncate">{activeDefinition.form.title}</h2>
            <p className="text-xs text-[var(--muted)] mt-1 truncate">{activeRequest.sourceItems?.map((source) => source.itemTitle).join(', ') || t('NLSHelperFormsDirectAssignment')}</p>
          </div>
          <Button
            variant="tertiary"
            size="md"
            className="shrink-0"
            onClick={() => {
              setActiveRequest(null)
              setIsFormDirty(false)
            }}
            icon={<ArrowBackRoundedIcon fontSize="inherit" />}
          >
            {t('NLSHelperFormsBack')}
          </Button>
        </div>
        <FormRenderer
          definition={activeDefinition.form}
          onDirtyChange={setIsFormDirty}
          disableSwitchGuard={true}
          onSubmitted={() => {
            setActiveRequest(null)
            setIsFormDirty(false)
          }}
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3 p-4" data-testid="helper-forms-panel">
      <div className="flex flex-col gap-2">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[var(--accent-strong)]">{t('NLSHelperFormsTitle')}</p>
          <h2 className="text-xl font-black tracking-tight text-[var(--text-strong)] mt-0.5">
            {getHelperFormCountLabel(visibleRequests.length, t)}
          </h2>
        </div>
        <div
          role="tablist"
          aria-label={t('NLSHelperFormsStatusAria')}
          className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-1"
        >
          {([
            { id: 'PENDING' as const, label: t('NLSHelperFormsStatusPending') },
            { id: 'COMPLETED' as const, label: t('NLSHelperFormsStatusCompleted') },
            { id: 'ALL' as const, label: t('NLSHelperFormsStatusAll') },
          ]).map((entry) => {
            const isActive = status === entry.id
            return (
              <Pressable
                key={entry.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setStatus(entry.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                  isActive
                    ? 'bg-[var(--accent-soft)] text-[var(--accent-strong)] border border-[color-mix(in_srgb,var(--accent)_35%,transparent)]'
                    : 'bg-[var(--surface-muted)] text-[var(--muted)] hover:text-[var(--text)] border border-transparent'
                }`}
              >
                {entry.label}
              </Pressable>
            )
          })}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        {visibleRequests.map((request) => (
          <Pressable
            key={request.id}
            className="flex flex-col gap-2 rounded-2xl border border-[var(--border-subtle)] bg-[var(--surface-card)] p-4 text-left shadow-[var(--shadow-card)] hover:border-[var(--border-strong)] transition-all"
            onClick={() => setActiveRequest(request)}
            data-testid={`helper-form-request-${request.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <span className="text-[11px] font-bold text-[var(--muted)]">{statusLabel(request.status, t)}</span>
                <h3 className="text-base font-bold text-[var(--text-strong)] truncate">{request.formTitle}</h3>
              </div>
              <span className="shrink-0 rounded-full border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[var(--muted)]">
                {request.sourceItems?.length ? `${request.sourceItems.length} items` : 'direct'}
              </span>
            </div>
            {(request as { instructions?: string }).instructions ? (
              <p className="text-xs text-[var(--muted)] line-clamp-2">{(request as { instructions?: string }).instructions}</p>
            ) : null}
            {request.sourceItems?.length ? (
              <div className="flex flex-wrap gap-1 pt-1">
                {request.sourceItems.map((source) => (
                  <span
                    key={source.itemId}
                    className="inline-flex items-center rounded-lg bg-[var(--surface-muted)] px-2 py-0.5 text-[11px] font-medium text-[var(--text)]"
                  >
                    {source.itemTitle}
                  </span>
                ))}
              </div>
            ) : null}
          </Pressable>
        ))}
        {visibleRequests.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-[var(--border-subtle)] p-6 text-center text-sm text-[var(--muted)]">
            {t('NLSHelperFormsEmptyFilterTitle')}
          </div>
        ) : null}
      </div>
    </div>
  )
}
